import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { BLACK, FEED_BACK, PRIMARY_PURPLE } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import formatAddress from '../../../../utils/formatAddress';
import SectionCard from '../../../positionTemplate/components/SectionCard';
import { getId, getJurisdiction, isValidWorkersCompCode } from '../../helpers';
import { getPositionTemplateDetail } from '../../positionTemplateDetailReducer';
import {
  buildAddPairingSave,
  buildRemovePairingSave,
  getPairedLocationIds,
  getPairingRows,
} from '../../workersCompAdapter';
import AsyncEntitySearch from '../AsyncEntitySearch';
import FieldLabel from '../FieldLabel';
import SaveStatusIndicator from '../SaveStatusIndicator';
import SectionTitle from '../SectionTitle';

import WorkersCompCodeInput from './WorkersCompCodeInput';

const GRID_COLUMNS = 'minmax(0, 1fr) 120px 220px 40px';

const rowSx = {
  alignItems: 'center',
  borderBottom: `1px solid ${BLACK[20]}`,
  columnGap: '16px',
  display: 'grid',
  gridTemplateColumns: GRID_COLUMNS,
  padding: '12px 16px',
};

const headerRowSx = { ...rowSx, paddingY: '10px' };

const titleRowSx = { alignItems: 'center', display: 'flex', gap: '12px' };

const addButtonSx = {
  borderColor: PRIMARY_PURPLE[70],
  borderRadius: '6px',
  color: PRIMARY_PURPLE[70],
  fontFamily: PRIMARY_FONT[500],
  textTransform: 'none',
};

const tableSx = { margin: '-8px -16px' };

const addFormSx = {
  borderBottom: `1px solid ${BLACK[20]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '12px 16px',
};

const addFormFieldsSx = {
  alignItems: 'start',
  columnGap: '16px',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 220px',
};

const fieldColumnSx = { display: 'flex', flexDirection: 'column', gap: '6px' };

const locationFieldColumnSx = { ...fieldColumnSx, minWidth: 0 };

const selectedLocationSx = {
  alignItems: 'center',
  border: `1px solid ${BLACK[30]}`,
  borderRadius: '6px',
  display: 'flex',
  gap: '8px',
  justifyContent: 'space-between',
  padding: '4px 4px 4px 12px',
};

const shrinkableSx = { minWidth: 0 };

const locationNameSx = { color: BLACK[100], fontFamily: PRIMARY_FONT[500], fontSize: '14px' };

const locationAddressSx = { color: BLACK[70], fontSize: '12px' };

const iconSx = { fontSize: '18px' };

const addFormActionsSx = { display: 'flex', gap: '8px', justifyContent: 'flex-end' };

const cancelButtonSx = { color: BLACK[80], fontFamily: PRIMARY_FONT[500], textTransform: 'none' };

const confirmButtonSx = {
  backgroundColor: PRIMARY_PURPLE[70],
  borderRadius: '6px',
  fontFamily: PRIMARY_FONT[500],
  textTransform: 'none',
  '&:hover': { backgroundColor: PRIMARY_PURPLE[80] },
};

const jurisdictionCellSx = { alignItems: 'center', display: 'flex', gap: '6px' };

const jurisdictionChipSx = {
  backgroundColor: BLACK[20],
  borderRadius: '12px',
  color: BLACK[90],
  fontFamily: PRIMARY_FONT[500],
  fontSize: '12px',
  padding: '2px 10px',
};

const lockIconSx = { color: BLACK[60], fontSize: '14px' };

const codeSx = { color: BLACK[100], fontFamily: 'monospace', fontSize: '14px' };

const codeNotSetSx = {
  color: FEED_BACK.ORANGE_70,
  fontFamily: PRIMARY_FONT[400],
  fontSize: '14px',
};

const footnoteSx = {
  alignItems: 'flex-start',
  backgroundColor: BLACK[10],
  color: BLACK[70],
  display: 'flex',
  fontSize: '12px',
  gap: '8px',
  padding: '12px 16px',
};

const footnoteIconSx = { fontSize: '16px' };

const footnoteTextSx = { fontSize: '12px' };

const LocationsWorkersCompCard = ({ positionTemplate, saveField, getStatus }) => {
  const intl = useIntl();
  const [isAdding, setIsAdding] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [draftCode, setDraftCode] = useState('');
  const dispatch = useDispatch();
  const rows = getPairingRows(positionTemplate);
  const corporationId = getId(positionTemplate.corporation);

  const closeAddForm = () => {
    setIsAdding(false);
    setSelectedLocation(null);
    setDraftCode('');
  };

  const openAddForm = () => setIsAdding(true);

  const canAdd = !!selectedLocation && isValidWorkersCompCode(draftCode);

  const submitAdd = () => {
    if (!canAdd) return;
    saveField({
      key: 'locations',
      ...buildAddPairingSave(positionTemplate, {
        location: selectedLocation,
        workersCompCode: draftCode,
      }),
    });
    closeAddForm();
  };

  const reloadTemplate = () =>
    dispatch(
      getPositionTemplateDetail({
        filters: [{ field: '_id', operation: 'equalsID', value: getId(positionTemplate) }],
      })
    );

  const removePairing = (pairingId) =>
    saveField({
      key: 'locations',
      ...buildRemovePairingSave(positionTemplate, pairingId),
      onError: (error) => {
        if (error?.response?.status === 404) reloadTemplate();
      },
    });

  const buildLocationSearch = useCallback(
    (searchTerm) => ({
      filters: [
        { field: 'corporation._id', operation: 'equalsID', value: corporationId },
        ...(searchTerm
          ? [{ field: 'description', operation: 'icontains', value: searchTerm }]
          : []),
      ],
    }),
    [corporationId]
  );

  return (
    <SectionCard
      header={
        <>
          <Box sx={titleRowSx}>
            <SectionTitle>{intl.formatMessage({ id: 'pt.detail.locations.title' })}</SectionTitle>
            <SaveStatusIndicator status={getStatus('locations')} />
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={isAdding ? closeAddForm : openAddForm}
            sx={addButtonSx}
          >
            {intl.formatMessage({ id: 'pt.detail.locations.add' })}
          </Button>
        </>
      }
    >
      <Box sx={tableSx}>
        <Box sx={headerRowSx}>
          <FieldLabel>
            {intl.formatMessage({ id: 'pt.detail.locations.column.location' })}
          </FieldLabel>
          <FieldLabel>
            {intl.formatMessage({ id: 'pt.detail.locations.column.jurisdiction' })}
          </FieldLabel>
          <FieldLabel>
            {intl.formatMessage({ id: 'pt.detail.locations.column.workersCompCode' })}
          </FieldLabel>
          <span />
        </Box>

        {isAdding && (
          <Box data-testid="pt-detail-add-location-form" sx={addFormSx}>
            <Box sx={addFormFieldsSx}>
              <Box sx={locationFieldColumnSx}>
                <FieldLabel>
                  {intl.formatMessage({ id: 'pt.detail.locations.column.location' })}
                </FieldLabel>
                {selectedLocation ? (
                  <Box data-testid="pt-detail-selected-location" sx={selectedLocationSx}>
                    <Box sx={shrinkableSx}>
                      <Typography sx={locationNameSx}>{selectedLocation.description}</Typography>
                      <Typography sx={locationAddressSx}>
                        {formatAddress(selectedLocation)}
                      </Typography>
                    </Box>
                    <IconButton
                      aria-label={intl.formatMessage({
                        id: 'pt.detail.locations.add.changeLocation',
                      })}
                      size="small"
                      onClick={() => setSelectedLocation(null)}
                    >
                      <CloseIcon sx={iconSx} />
                    </IconButton>
                  </Box>
                ) : (
                  <AsyncEntitySearch
                    testId="pt-detail-add-location"
                    autoFocus
                    placeholder={intl.formatMessage({ id: 'pt.detail.locations.search' })}
                    route="locations/read"
                    buildRequestData={buildLocationSearch}
                    optionLabel="description"
                    renderOptionSecondary={formatAddress}
                    excludeIds={getPairedLocationIds(positionTemplate)}
                    onSelect={setSelectedLocation}
                  />
                )}
              </Box>
              <Box sx={fieldColumnSx}>
                <FieldLabel htmlFor="pt-detail-new-location-code">
                  {intl.formatMessage({ id: 'pt.detail.locations.column.workersCompCode' })}
                </FieldLabel>
                <WorkersCompCodeInput
                  id="pt-detail-new-location-code"
                  value={draftCode}
                  onChange={setDraftCode}
                  onSubmit={submitAdd}
                />
              </Box>
            </Box>
            <Box sx={addFormActionsSx}>
              <Button onClick={closeAddForm} sx={cancelButtonSx}>
                {intl.formatMessage({ id: 'button.cancel' })}
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={!canAdd}
                onClick={submitAdd}
                sx={confirmButtonSx}
              >
                {intl.formatMessage({ id: 'pt.detail.locations.add.confirm' })}
              </Button>
            </Box>
          </Box>
        )}

        {rows.map(({ pairingId, locationId, location, workersCompCode }) => {
          const jurisdiction = getJurisdiction(location);
          const isLastLocation = rows.length === 1;
          const locationName =
            location?.description ||
            intl.formatMessage({ id: 'pt.detail.locations.loadingLocation' });
          const deleteLabel = intl.formatMessage(
            { id: 'pt.detail.locations.remove' },
            { location: locationName }
          );

          return (
            <Box key={pairingId || locationId} data-testid="pt-detail-location-row" sx={rowSx}>
              <Box sx={shrinkableSx}>
                <Typography sx={locationNameSx}>{locationName}</Typography>
                {location && (
                  <Typography sx={locationAddressSx}>{formatAddress(location)}</Typography>
                )}
              </Box>
              <Box sx={jurisdictionCellSx}>
                {jurisdiction && <Box sx={jurisdictionChipSx}>{jurisdiction}</Box>}
                <LockOutlinedIcon sx={lockIconSx} />
              </Box>
              <Typography
                data-testid="pt-detail-location-code"
                sx={workersCompCode ? codeSx : codeNotSetSx}
              >
                {workersCompCode ||
                  intl.formatMessage({ id: 'pt.detail.locations.workersCompCode.notSet' })}
              </Typography>
              <Tooltip
                title={
                  isLastLocation
                    ? intl.formatMessage({ id: 'pt.detail.locations.remove.lastLocation' })
                    : ''
                }
              >
                <span>
                  <IconButton
                    aria-label={deleteLabel}
                    disabled={isLastLocation || !pairingId}
                    onClick={() => removePairing(pairingId)}
                    size="small"
                  >
                    <DeleteOutlineIcon sx={iconSx} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}

        <Box sx={footnoteSx}>
          <InfoOutlinedIcon sx={footnoteIconSx} />
          <Typography sx={footnoteTextSx}>
            {intl.formatMessage(
              { id: 'pt.detail.locations.footnote' },
              { b: (chunks) => <strong key="b">{chunks}</strong> }
            )}
          </Typography>
        </Box>
      </Box>
    </SectionCard>
  );
};

LocationsWorkersCompCard.propTypes = {
  positionTemplate: PropTypes.shape({
    corporation: PropTypes.shape({}),
    locations: PropTypes.arrayOf(PropTypes.shape({})),
    locationAndWorkersCompPairings: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        locationId: PropTypes.string,
        workersCompCode: PropTypes.string,
      })
    ),
  }).isRequired,
  saveField: PropTypes.func.isRequired,
  getStatus: PropTypes.func.isRequired,
};

export default LocationsWorkersCompCard;
